import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { SmartConnectorContext } from '../../context';
import { useTranslation } from 'react-i18next';
import { KizenTypography } from '__app/typography';
import Button from '__components/Button';
import InputControl from '__components/Inputs/InputControl';
import { useToast, toastVariant } from '__components/ToastProvider';
import useModal from '__components/Modals/useModal';
import { SmartConnector, SmartConnectorDTO } from 'pages/SmartConnectors/types';
import { getIsSmartConnectorActive } from 'pages/SmartConnectors/helpers';
import {
  SMART_CONNECTOR_STATUSES,
  SMART_CONNECTOR_STEPS,
} from 'pages/SmartConnectors/constants';
import SmartConnectorService from 'services/SmartConnectorService';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import { getStepData } from 'pages/SmartConnectors/SmartConnector/helpers';
import {
  StyledReferenceButton,
  StyledSecretsAndParametersContent,
} from './styles';
import {
  SecretsAndParametersErrors,
  SecretsAndParametersModal,
} from './SecretsAndParametersModal';
import { getOriginalError } from 'services/AxiosService';
import { AxiosError } from 'axios';

export const SecretsAndParameters = ({
  setForceResetScript,
}: {
  setForceResetScript: Dispatch<SetStateAction<number>>;
}) => {
  const { t } = useTranslation();
  const {
    smartConnector,
    refetchSmartConnector,
    refreshing,
    handleSave,
    setStepData,
    isDirty,
    setTemplateScript,
  } = useContext(SmartConnectorContext);
  const [showToast] = useToast();

  const [modalProps, , { show: showModal }] = useModal({
    handleSubmit: async (
      data: Pick<SmartConnector, 'integration_secrets' | 'sql_parameters'>
    ) => {
      const payload: SmartConnectorDTO = {
        integration_secrets: data.integration_secrets,
        sql_parameters: data.sql_parameters,
      };
      if (getIsSmartConnectorActive(smartConnector)) {
        payload.status = SMART_CONNECTOR_STATUSES.inactive;
      }

      await SmartConnectorService.updateSmartConnector(
        smartConnector.id,
        payload
      );

      const {
        data: { user_script },
      } = await SmartConnectorService.getScriptTemplate(
        smartConnector.id,
        smartConnector.source_file!.id
      );

      setTemplateScript(user_script);
      setForceResetScript((prev) => prev + 1);

      await refetchSmartConnector();
    },
    handleError: (axiosError: AxiosError) => {
      const {
        integration_secrets,
        sql_parameters,
      }: SecretsAndParametersErrors = getOriginalError(axiosError) || {};
      setErrors({
        integration_secrets: integration_secrets || [],
        sql_parameters: sql_parameters || [],
      });

      showToast({
        message: t('Failed to update Secrets and SQL Parameters'),
        variant: toastVariant.FAILURE,
      });
    },
    closeOnSubmit: false,
  });

  const [errors, setErrors] = useState<SecretsAndParametersErrors>({});

  const [
    confirmationModalProps,
    ,
    { show: showConfirmationModal, hide: hideConfirmationModal },
  ] = useModal({
    handleSubmit: () => {
      setStepData(
        getStepData(smartConnector, SMART_CONNECTOR_STEPS.connection)
      );
      setForceResetScript((prev) => prev + 1);
      if (smartConnector.sql_enabled) {
        showModal();
      }
    },
  });

  const handleOpenModal = () => {
    setErrors({});
    if (isDirty) {
      showConfirmationModal();
    } else {
      showModal();
    }
  };

  const onConfirmSave = async () => {
    const [updated] = await handleSave(false, true);
    if (updated) {
      hideConfirmationModal();
      showModal();
    }
  };

  return (
    <>
      <InputControl
        label={t('Secrets and SQL Parameters')}
        labelInfo={t(
          'Integration Secrets allow you to securely use credentials and passwords to external systems. SQL Parameters can be used to set configuration options'
        )}
        labelInfoPlacement="top"
        variant="outline"
        className=""
      >
        <StyledSecretsAndParametersContent>
          {smartConnector.integration_secrets?.length ? (
            <KizenTypography>
              {t('{{amount}} Secrets', {
                amount: smartConnector.integration_secrets.length,
              })}
            </KizenTypography>
          ) : null}
          {smartConnector.sql_parameters &&
          Object.keys(smartConnector.sql_parameters).length ? (
            <KizenTypography>
              {t('{{amount}} Parameters', {
                amount: Object.keys(smartConnector.sql_parameters).length,
              })}
            </KizenTypography>
          ) : null}
          <StyledReferenceButton
            variant="text"
            color="blue"
            onClick={handleOpenModal}
          >
            {t('Configure')}
          </StyledReferenceButton>
        </StyledSecretsAndParametersContent>
      </InputControl>
      {modalProps.show ? (
        <SecretsAndParametersModal
          {...modalProps}
          errors={errors}
          setErrors={setErrors}
        />
      ) : null}
      <ConfirmationModal
        heading={t('You Have Unsaved Changes')}
        buttonText={t('Discard Changes')}
        defaultLeftBtnText={t('Save Changes')}
        actionBtnColor="red"
        leftBtn={<Button disabled={refreshing} />}
        defaultLeftBtnHandler={onConfirmSave}
        {...confirmationModalProps}
      >
        {t(
          'You have unsaved changes. Would you like to save them or discard them before continuing?'
        )}
      </ConfirmationModal>
    </>
  );
};
